<template>
  <v-expansion-panels v-model="ordersPanel">
    <v-expansion-panel>
      <v-expansion-panel-header class="text-h6 font-weight-regular">
        <v-row>
          <v-col md="auto">
            {{ $t('ORDERS.ITEMS') }}
          </v-col>
          <v-spacer></v-spacer>
          <v-col md="auto">
            <div
              v-if="isPersonWithoutRoles"
              class="mr-2 text-subtitle-1 grey--text"
            >
              {{ $t('ORDERS.PERSON_ACCESS') }}
            </div>
          </v-col>
          <v-spacer />
          <v-col md="auto">
            <v-btn
              :disabled="isPersonWithoutRoles"
              class="mr-4"
              outlined
              color="indigo"
              @click.stop="confirmCreateOrder"
            >
              <v-icon left>mdi-plus</v-icon>
              {{ $t('ITEM.NEW') }} {{ $t('ORDERS.ITEM') }}
            </v-btn>

            <default-dialog
              v-model="createOrderDialog"
              @agree="createOrder"
              @cancel="createOrderDialog.type = {}"
            >
              <template v-slot:card>
                <v-card-text>
                  <types-list
                    :list="orderTypes"
                    @input="createOrderDialog.type = $event || {}"
                  />
                </v-card-text>
              </template>

              <template v-slot:title>
                <v-toolbar-title>
                  {{ $t('ORDERS.LIST.TITLE_PREFIX') }}
                  <strong>{{ $t('ORDERS.ITEM') }}</strong>
                </v-toolbar-title>
              </template>

              <template v-slot:subtitle>
                <v-card-subtitle>
                  {{ $t('ORDERS.LIST.SUBTITLE_PREFIX') }}
                  <strong>{{ $t('ORDERS.ITEM') }}</strong>
                </v-card-subtitle>
              </template>
            </default-dialog>
          </v-col>
        </v-row>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <orders-list
          type="c"
          :items="items"
          :meta="meta"
          :options.sync="options"
          @show="showOrder"
        />
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import { debounce, isEqual } from 'lodash';
import { mapActions } from 'vuex';
import errors from '@/modules/_global/mixins/errors';
import { getViewRouteNameByOrderType } from '@/modules/orders/common/utils';
import DefaultDialog from '@/modules/_global/_components/DefaultDialog';
import TypesList from '@/modules/_global/_components/TypesList';
import {
  BUSINESS,
  REPETITIVE_CUSTOMER
} from '@/modules/orders/common/order-types';
import listOptions, {
  DEBOUNCE_DELAY
} from '@/modules/_global/mixins/list-options';

export default {
  name: 'PersonOrdersList',

  components: {
    TypesList,
    DefaultDialog,
    OrdersList: () => import('@/modules/orders/components/OrdersList')
  },

  mixins: [errors, listOptions],

  model: {
    prop: 'person'
  },

  props: {
    person: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      ordersPanel: 0,
      orders: undefined,
      error: null,
      localPerson: {
        without_vat: false
      },
      createOrderDialog: null,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['date_entered'],
        sortDesc: [true],
        groupBy: [],
        groupDesc: [],
        mustSort: false,
        multiSort: false
      }
    };
  },

  computed: {
    orderTypes() {
      return [
        {
          name: this.$t('ORDERS.ITEM'),
          description: `${this.$t(
            'ORDERS.LIST.ITEM_DESCRIPTION_PREFIX'
          )} ${this.$t('ORDERS.LIST.ITEMS.ORDER')}`,
          id: BUSINESS,
          route: 'customer-order.create'
        },
        {
          name: this.$t('ORDERS.REPETITIVE.ITEM'),
          description: `${this.$t(
            'ORDERS.LIST.ITEM_DESCRIPTION_PREFIX'
          )} ${this.$t('ORDERS.LIST.ITEMS.REPETITIVE')}`,
          id: REPETITIVE_CUSTOMER,
          route: 'customer-order-repetitive.create'
        }
      ];
    },
    isPersonWithoutRoles() {
      return !(
        this.person.user &&
        this.person.user.roles &&
        this.person.user.roles.length
      );
    }
  },

  watch: {
    person: {
      deep: true,
      immediate: true,
      handler: function(person) {
        if (isEqual(person, this.localPerson)) {
          return;
        }

        this.localPerson = {
          ...this.localPerson,
          ...person
        };
      }
    }
  },

  created() {
    this.refreshData();
  },

  methods: {
    ...mapActions('person', ['getOrders']),

    getListData: debounce(function getDebouncedListData() {
      this.getOrders({
        personId: this.person.id,
        params: this.params.paginationOptions
      }).then(({ data }) => {
        this.items = data.data;
        this.meta = data.meta;
      });
    }, DEBOUNCE_DELAY),

    showOrder(order) {
      const routeName = getViewRouteNameByOrderType(order.type_id);

      if (routeName) {
        this.$router.push({
          name: routeName,
          params: { id: order.id }
        });
      }
    },

    createOrder() {
      if (!this.createOrderDialog && !this.createOrderDialog.type) {
        return;
      }
      this.$router.push({
        name: this.createOrderDialog.type.route,
        params: { personId: this.person.id }
      });
    },

    confirmCreateOrder() {
      this.createOrderDialog = {
        dialog: true,
        options: { agreeBtnText: this.$t('BUTTON.CREATE') }
      };
    }
  }
};
</script>

<style scoped></style>
